import { Workflow, WorkflowService } from "@/generated";
import { useForm } from "react-hook-form";
import {
  IWorkflowEditFormData,
  WorkflowEditForm,
} from "../Form/Forms/WorkflowEditForm";
import { useState } from "react";
import { useSpotUser } from "@/utils/spotUser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SpotToast } from "../SpotToast";
import { DATA_KEYS } from "@/utils/useRemoteData";
import BaseDialog from "./BaseDialog";

interface IEditModal {
  isOpen: boolean;
  onClose: () => void;
  workflow: Workflow;
}

export const WorkflowEditModal = ({
  isOpen,
  onClose,
  workflow,
}: IEditModal) => {
  const [isLoading, setIsLoading] = useState(false);
  const { spotGroup } = useSpotUser();

  const queryClient = useQueryClient();

  const { control, formState, handleSubmit } = useForm<IWorkflowEditFormData>({
    defaultValues: {
      title: workflow.name || "",
      description: workflow.description || "",
      icon: (workflow.meta?.icon as string) || "workflow",
    },
    mode: "onChange",
  });

  const { isValid, isDirty, errors } = formState;

  const editWorkflow = useMutation({
    mutationFn: WorkflowService.updateWorkflow,
    onSuccess: () => {
      SpotToast.success("Flow updated successfully");
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.WORKFLOWS_ID] });
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.WORKFLOWS] });
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.WORKSHOP_RECENT] });
      onClose();
    },
    onError: () => {
      SpotToast.error(`Error updating flow`);
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  const handleFormSubmit = (data: IWorkflowEditFormData) => {
    setIsLoading(true);

    const body: any = {};

    // Only include fields that have changed
    if (data.title !== workflow.name) {
      body.name = data.title === "" ? "Untitled" : data.title;
    }

    if (data.description !== workflow.description) {
      body.description = data.description;
    }

    const currentIcon = (workflow.meta?.icon as string) || "workflow";
    if (data.icon !== currentIcon) {
      body.meta = { icon: data.icon };
    }

    editWorkflow.mutate({
      path: {
        workflow_id: workflow.id,
        group_id: spotGroup.id,
      },
      body,
    });
  };

  const footerActions = [
    {
      label: "Cancel",
      props: { variant: "outline", onClick: onClose },
    },
    {
      label: "Save",
      props: {
        type: "submit",
        onClick: handleSubmit(handleFormSubmit),
        loading: isLoading,
        disabled: !isDirty || !isValid,
      },
    },
  ];

  return (
    <BaseDialog
      title="Flow details"
      isOpen={isOpen}
      onOpenChange={onClose}
      footerActions={footerActions}
    >
      <WorkflowEditForm
        control={control}
        errors={errors}
        forcedName={workflow.name}
        onSubmit={handleSubmit(handleFormSubmit)}
      />
    </BaseDialog>
  );
};
